import { createModel } from '@rematch/core';
import { generatePath } from 'react-router-dom';
import { toast } from 'react-toastify';

// Constants
import URL from '../../constants/api-constants';

// Types
import type { RootModel } from './index';

// Http
import http from '../http/http-common';
import { IChannelConsoleSetup, IChannelData, IChannelFaultsData, IChannelName } from '../../types/channel-types';
import { ChangeChannelAction } from '../../enums/action';
import axios, { AxiosError, AxiosRequestConfig, GenericAbortSignal } from 'axios';

export type ChannelActionItem = {
  controller_serial_number: string;
  channel_num: string;
  value?: string;
  manualFaultsOnly?: boolean;
};

export type MultiChannelActionPayload = {
  action: ChangeChannelAction;
  list: ChannelActionItem[];
};

export type ChannelColor = {
  itemNumber: number;
  color: string | null;
};

const DEFAULT_STATE = {
  channels: [] as IChannelName[],
  channel: null as IChannelData | null,
  channelFaults: null as IChannelFaultsData | null,
  channelsFaults: [] as IChannelFaultsData[],
  channelColors: [] as ChannelColor[],
};

export const channels = createModel<RootModel>()({
  state: {
    channels: [] as IChannelName[],
    channel: null as IChannelData | null,
    channelFaults: null as IChannelFaultsData | null,
    channelsFaults: [] as IChannelFaultsData[],
    channelColors: [] as ChannelColor[],
  },
  reducers: {
    set(state, payload) {
      return {
        ...state,
        ...payload,
      };
    },
    resetChannelState(state) {
      return {
        ...state,
        channels: [] as IChannelName[],
        channel: null as IChannelData | null,
      };
    },
    clearChannel(state) {
      return {
        ...state,
        channel: null,
      };
    },
    resetState() {
      return DEFAULT_STATE;
    },
  },
  effects: (dispatch) => {
    return {
      async getChannels(controller_id: string) {
        try {
          const response = await http.get(generatePath(URL.CONTROLLER_CHANNELS, { id: controller_id }));

          if (response.status === 200) {
            dispatch.channels.set({
              channels: response.data,
            });

            return response.data;
          }

          return;
        } catch (error: any) {
          toast.error(error.message);
        }
      },

      async getChannelsConsoleSetup(controller_id: string): Promise<IChannelConsoleSetup[] | undefined> {
        try {
          const response = await http.get<IChannelConsoleSetup[]>(
            generatePath(URL.CONTROLLER_CHANNELS_CONSOLE_SETUP, { id: controller_id }),
          );

          return response.data;
        } catch (error: any) {
          toast.error(error.message);
        }
      },

      async getChannelColors(controller_id: string) {
        try {
          const response = await http.get<ChannelColor[]>(
            generatePath(URL.CONTROLLER_CHANNEL_COLORS, { id: controller_id }),
          );

          dispatch.channels.set({
            channelColors: response.data,
          });

          return response.data;
        } catch (error: any) {
          toast.error(error.message);
        }
      },

      async updateChannelColors(params: { controller_id: string; colors: { itemNumber: number; color: string }[] }) {
        try {
          const response = await http.put<ChannelColor[]>(
            generatePath(URL.CONTROLLER_CHANNEL_COLORS, { id: params.controller_id }),
            { colors: params.colors },
          );

          dispatch.channels.set({
            channelColors: response.data,
          });

          return response.data;
        } catch (error: any) {
          toast.error(error.message);
          throw error;
        }
      },

      async getChannel(params: { controller_id: string; channel_num: number; signal?: GenericAbortSignal }) {
        try {
          const path = generatePath(URL.CONTROLLER_CHANNEL, {
            id: params.controller_id,
            channel_num: params.channel_num,
          });
          const config: AxiosRequestConfig<{ signal?: GenericAbortSignal }> = {};
          if (params?.signal) {
            config.signal = params.signal;
          }

          const response = await http.get(path, config);
          dispatch.channels.set({
            channel: response.data,
          });

          return {
            channel: response.data,
          };
        } catch (error) {
          if (axios.isCancel(error)) {
            return;
          }

          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getChannelFaults(params: { controller_id: string; channel_num: number }) {
        try {
          const response = await http.get<IChannelFaultsData>(
            generatePath(URL.CONTROLLER_CHANNEL_FAULTS, {
              id: params.controller_id,
              channel_num: params.channel_num,
            }),
          );
          dispatch.channels.set({
            channelFaults: response.data,
          });

          return {
            channelFaults: response.data,
          };
        } catch (error) {
          toast.error(error.message);
        }
      },

      async getListChannelFaults(params: { channel_ids: string[] }) {
        try {
          const response = await http.get<IChannelFaultsData[]>(generatePath(URL.CONTROLLERS_CHANNELS_FAULTS), {
            params: {
              channel_ids: params.channel_ids,
            },
          });
          dispatch.channels.set({
            channelsFaults: response.data,
          });

          return {
            channelsFaults: response.data,
          };
        } catch (error) {
          toast.error(error.message);
        }
      },

      async updateChannels(payload: { cycleId: string; data: MultiChannelActionPayload }) {
        try {
          const path = generatePath(URL.CYCLE_CHANNELS, { id: payload.cycleId });
          await http.put(path, payload.data);

          toast.success('Channels updated successfully');

          return;
        } catch (error: any) {
          toast.error(error.message);
        }
      },

      async updateChannel({ id, formData }) {
        try {
          await http.put(generatePath(URL.CONTROLLER, { id }), formData);

          toast.success('Channel updated successfully');
        } catch (error) {
          toast.error(error.message);
        }
      },
    };
  },
});
